import useAgency from 'features/Agency/useAgency';
import ChargeHelper from 'features/Charge/chargeHelper';
import imgFileArrowUp from 'assets/images/Dialogs/img_file-arrow-up.png';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import * as ExcelJS from 'exceljs';

DialogUploadIdentifierView.propTypes = {
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

const ALLOWED_EXTENSIONS = ['.xls', '.xlsx', '.xlsm'];

const isValidExcelFile = (file) => {
  if (!file) return false;
  const name = file.name?.toLowerCase() ?? '';
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

function DialogUploadIdentifierView({ onCancel, onDone }) {
  const { operatorList } = useAgency();
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const selectedOperatorName = _.find(operatorList, (op) => op.operatorId?.toString() === selectedOperatorId?.toString())?.name;

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!isValidExcelFile(file)) {
      ToastHelper.showError('Định dạng file không hợp lệ. Chỉ chấp nhận .xls, .xlsx, .xlsm');
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Mã định danh');
    const headers = ['Mã MAC/EVCC'];
    const headerRow = sheet.getRow(1);
    headers.forEach((h, i) => {
      headerRow.getCell(i + 1).value = h;
      headerRow.getCell(i + 1).font = { name: 'Times New Roman', bold: true, size: 11 };
      headerRow.getCell(i + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      headerRow.getCell(i + 1).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });
    sheet.getColumn(1).width = 30;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'mau_ma_dinh_danh.xlsx';
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      ToastHelper.showError('Vui lòng chọn tệp excel');
      return;
    }
    setUploading(true);
    try {
      const result = await ChargeHelper.API.Identifier.requestUploadIdentifierExcel(selectedOperatorId || null, selectedFile);

      if (result === true) {
        ToastHelper.showSuccess('Tải lên dữ liệu thành công');
        if (onDone) onDone();
      } else {
        ToastHelper.showError(_.isString(result) ? result : 'Tải lên thất bại');
      }
    } catch {
      ToastHelper.showError('Có lỗi xảy ra, vui lòng thử lại');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="px-4 pt-4 pb-2">
        {/* operator dropdown */}
        <div className="mb-4">
          {UIHelper.buildFormLabel('Đối tác sạc')}
          <KTBSDropdown
            name="operatorId_upload"
            toggleElement={
              <button
                className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                type="button"
              >
                {selectedOperatorName ?? 'Chọn đối tác sạc'}
              </button>
            }
            selectedValue={selectedOperatorId}
            dropdownMenuItems={[
              { name: 'Chọn đối tác sạc', value: '' },
              ...(operatorList ?? []).map((op) => ({ name: op.name, value: op.operatorId?.toString() })),
            ]}
            dropdownMenuClassName="overflow-auto w-100"
            dropdownMenuStyle={{ maxHeight: '30vh' }}
            onChange={(value) => setSelectedOperatorId(value)}
            isDropdownForm={true}
          />
        </div>

        {/* template link */}
        <p style={{ fontSize: 13, color: AppColors.gray[600], marginBottom: 12 }}>
          Các dữ liệu trong tệp excel phải được chuẩn hóa theo mẫu sau:{' '}
          <span
            className="cursor-pointer"
            style={{ color: AppColors.primary, fontWeight: 600, textDecoration: 'underline' }}
            onClick={handleDownloadTemplate}
          >
            Tải tệp excel mẫu
          </span>
        </p>

        {/* upload zone label */}
        {UIHelper.buildFormLabel('Tải lên tệp excel đã được chuẩn hóa để nhập dữ liệu')}

        {/* drop zone */}
        <div
          className="mb-3"
          style={{
            border: `1px dashed ${dragging ? AppColors.primary : '#30BD6F'}`,
            borderRadius: 8,
            background: 'rgba(48, 189, 111, 0.05)',
            transition: 'all 0.2s',
          }}
        >
          <div
            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
            style={{ padding: '32px 16px' }}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            {uploading ? (
              <>
                <div className="spinner-border text-success mb-2" style={{ width: 32, height: 32 }} />
                <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[500])}>
                  Dữ liệu đang được tải lên
                </span>
              </>
            ) : (
              <>
                <img src={imgFileArrowUp} alt="" style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: 8 }} />
                <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                  Kéo thả hoặc ấn để tải lên
                </span>
                <span style={UIHelper.buildFontStyle('400', '12px', AppColors.gray[400])}>
                  Định dạng: .xls, .xlsx, .xlsm
                </span>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.xlsm"
            hidden
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
          />
        </div>

        {/* selected file */}
        {selectedFile && (
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{ background: AppColors.gray[50], borderRadius: 6, border: `1px solid ${AppColors.gray[200]}` }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="fa-solid fa-file-excel" style={{ color: '#1D6F42', fontSize: 16 }} />
              <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[700])}>
                {selectedFile.name}
              </span>
            </div>
            <button
              onClick={() => setSelectedFile(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: AppColors.gray[400], fontSize: 14 }}
            >
              <i className="fa-regular fa-xmark" />
            </button>
          </div>
        )}
      </div>

      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
        {UIHelper.buildGreenButton('Tải lên', handleSubmit, null, false, uploading)}
      </div>
    </div>
  );
}

export default DialogUploadIdentifierView;
